import {
  useEffect,
  useRef,
} from "react";

import {
  AnimatePresence,
  motion,
} from "framer-motion";

import {
  Link,
  useLocation,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import siteConfig from "../../../site/config/site";

import {
  getRoutePath,
} from "../../../site/config/routes";

import LanguageToggle from "../common/LanguageToggle";
import ThemeToggle from "../common/ThemeToggle";
import SocialLinks from "../common/SocialLinks";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  MobileMenu
  ============================================================

  Fullscreen editorial -mobiilimenu.

  Tavoite:
  - koko menu mahtuu yhteen näkymään
  - ei näkyvää sisäistä scrollbaria
  - linkit säilyvät suurina ja editorial-henkisinä
  - erittäin matalilla näytöillä sisältö tiivistyy
  - reitin vaihto sulkee menun
  - ESC sulkee menun
*/

export default function MobileMenu({
  isOpen,
  onClose,

  navigationItems,

  language,
  currentRouteId,

  theme,
  themes,
  setTheme,
}) {
  const {
    t,
  } = useTranslation("common");

  const location =
    useLocation();

  const mobileMenuConfig =
    siteConfig.mobileMenu || {};

  /*
    ============================================================
    CLOSE ON REAL ROUTE CHANGE
    ============================================================

    Ensimmäisellä renderillä ei suljeta valikkoa.

    Valikko suljetaan vasta silloin, kun URL-polku
    oikeasti muuttuu käyttäjän navigoidessa toiselle sivulle.
  */

  const previousPathnameRef =
    useRef(
      location.pathname
    );

  useEffect(() => {
    if (
      previousPathnameRef.current ===
      location.pathname
    ) {
      return;
    }

    previousPathnameRef.current =
      location.pathname;

    onClose();
  }, [
    location.pathname,
    onClose,
  ]);

  /*
    ============================================================
    BODY SCROLL LOCK
    ============================================================

    Estetään taustalla olevan sivun scrollaus
    fullscreen-menun ollessa auki.
  */

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [
    isOpen,
  ]);

  /*
    ============================================================
    ESC CLOSE
    ============================================================
  */

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handleKeyDown(
      event
    ) {
      if (
        event.key ===
        "Escape"
      ) {
        onClose();
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    isOpen,
    onClose,
  ]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="
            fixed
            inset-0
            z-[100]
            overflow-hidden
            bg-canvas
            text-text
            lg:hidden
          "
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
          transition={{
            duration: 0.35,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
        >
          <div
            className="
              mx-auto
              flex
              h-[100dvh]
              min-h-0
              w-full
              max-w-editorial
              flex-col
              px-6
              py-4

              sm:px-10
              sm:py-5

              [@media(max-height:700px)]:py-3
            "
          >
            {/* =================================================
                MENU HEADER
                ================================================= */}

            <div
              className="
                flex
                shrink-0
                items-center
                justify-between
                gap-6
                border-b
                border-border
                pb-4

                sm:pb-5

                [@media(max-height:700px)]:pb-3
              "
            >
              <Link
                to={getRoutePath(
                  "home",
                  language
                )}
                className="
                  font-display
                  text-lg
                  tracking-[0.04em]
                  transition-opacity
                  duration-300
                  hover:opacity-70
                "
                onClick={
                  onClose
                }
              >
                {
                  siteConfig.brand
                    .name
                }
              </Link>

              <button
                type="button"
                onClick={
                  onClose
                }
                className="
                  group
                  flex
                  h-11
                  w-11
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  border
                  border-border
                  transition
                  duration-300
                  hover:border-text

                  [@media(max-height:700px)]:h-9
                  [@media(max-height:700px)]:w-9
                "
                aria-label={t(
                  "controls.closeMenu"
                )}
              >
                <span
                  className="
                    relative
                    block
                    h-5
                    w-5
                  "
                  aria-hidden="true"
                >
                  <span
                    className="
                      absolute
                      left-1/2
                      top-1/2
                      h-px
                      w-5
                      -translate-x-1/2
                      -translate-y-1/2
                      rotate-45
                      bg-current
                    "
                  />

                  <span
                    className="
                      absolute
                      left-1/2
                      top-1/2
                      h-px
                      w-5
                      -translate-x-1/2
                      -translate-y-1/2
                      -rotate-45
                      bg-current
                    "
                  />
                </span>
              </button>
            </div>

            {/* =================================================
                EDITORIAL CONTENT
                ================================================= */}

            <div
              className="
                grid
                min-h-0
                flex-1
                items-center
                gap-6
                py-4

                sm:py-5

                md:grid-cols-[1.15fr_0.85fr]
                md:py-6

                [@media(max-height:700px)]:py-2
              "
            >
              {/* =================================================
                  LINKS
                  ================================================= */}

              <nav
                className="
                  min-h-0
                  w-full
                "
                aria-label={
                  language === "en"
                    ? "Mobile navigation"
                    : "Mobiilinavigaatio"
                }
              >
                <div
                  className="
                    flex
                    flex-col
                  "
                >
                  {navigationItems.map(
                    (
                      item,
                      index
                    ) => {
                      const itemPath =
                        getRoutePath(
                          item.routeId,
                          language
                        );

                      const isActive =
                        currentRouteId ===
                        item.routeId;

                      const number =
                        String(
                          index + 1
                        ).padStart(
                          2,
                          "0"
                        );

                      return (
                        <motion.div
                          key={
                            item.id
                          }
                          initial={{
                            opacity:
                              0,

                            y:
                              18,
                          }}
                          animate={{
                            opacity:
                              1,

                            y:
                              0,
                          }}
                          transition={{
                            duration:
                              0.55,

                            delay:
                              0.05 +
                              index *
                                0.055,

                            ease: [
                              0.22,
                              1,
                              0.36,
                              1,
                            ],
                          }}
                        >
                          <Link
                            to={
                              itemPath
                            }
                            onClick={
                              onClose
                            }
                            className="
                              group
                              flex
                              items-baseline
                              gap-3
                              border-b
                              border-border
                              py-3

                              sm:gap-4
                              sm:py-3.5

                              [@media(max-height:700px)]:py-2
                            "
                          >
                            {mobileMenuConfig.showNumbers !==
                              false && (
                              <span
                                className="
                                  w-7
                                  shrink-0
                                  text-[9px]
                                  uppercase
                                  tracking-[0.18em]
                                  text-muted

                                  sm:w-8
                                  sm:text-[10px]
                                "
                              >
                                {
                                  number
                                }
                              </span>
                            )}

                            <span
                              className={`
                                min-w-0
                                font-display
                                text-[clamp(1.85rem,7.5vw,3.5rem)]
                                leading-[0.9]
                                tracking-[-0.035em]
                                transition
                                duration-300

                                sm:text-[clamp(2.1rem,7vw,3.7rem)]

                                [@media(max-height:700px)]:text-[clamp(1.55rem,6vw,2.75rem)]

                                ${
                                  isActive
                                    ? `
                                      translate-x-1
                                      opacity-100
                                    `
                                    : `
                                      opacity-65
                                      group-hover:translate-x-1
                                      group-hover:opacity-100
                                    `
                                }
                              `}
                            >
                              {t(
                                item.labelKey
                              )}
                            </span>
                          </Link>
                        </motion.div>
                      );
                    }
                  )}
                </div>
              </nav>

              {/* =================================================
                  OPTIONAL EDITORIAL IMAGE
                  ================================================= */}

              {mobileMenuConfig.showImage &&
                mobileMenuConfig.image && (
                  <motion.figure
                    className="
                      hidden
                      max-h-full
                      overflow-hidden
                      md:block
                    "
                    initial={{
                      opacity: 0,
                      scale: 1.02,
                    }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                    }}
                    transition={{
                      duration: 0.9,
                      delay: 0.18,
                      ease: [
                        0.22,
                        1,
                        0.36,
                        1,
                      ],
                    }}
                  >
                    <img
                      src={
                        mobileMenuConfig
                          .image
                      }
                      alt={
                        mobileMenuConfig
                          .imageAlt ||
                        ""
                      }
                      className="
                        aspect-[4/5]
                        max-h-[52dvh]
                        w-full
                        object-cover
                      "
                    />
                  </motion.figure>
                )}
            </div>

            {/* =================================================
                MENU FOOTER
                ================================================= */}

            <motion.div
              className="
                flex
                shrink-0
                flex-col
                gap-3
                border-t
                border-border
                py-3

                sm:flex-row
                sm:items-center
                sm:justify-between
                sm:gap-5
                sm:py-4

                [@media(max-height:700px)]:py-2
              "
              initial={{
                opacity: 0,
                y: 12,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.5,
                delay: 0.25,
                ease: [
                  0.22,
                  1,
                  0.36,
                  1,
                ],
              }}
            >
              <div
                className="
                  flex
                  min-w-0
                  flex-col
                  gap-2
                "
              >
                {mobileMenuConfig.showLocation !==
                  false && (
                  <p
                    className="
                      m-0
                      text-[9px]
                      uppercase
                      tracking-[0.2em]
                      text-muted

                      sm:text-[10px]
                    "
                  >
                    {
                      siteConfig.brand
                        .location
                    }
                  </p>
                )}

                {mobileMenuConfig.showSocials !==
                  false && (
                  <SocialLinks
                    compact
                  />
                )}
              </div>

              <div
                className="
                  flex
                  shrink-0
                  flex-wrap
                  items-center
                  gap-2

                  sm:gap-3
                "
              >
                <LanguageToggle
                  currentLanguage={
                    language
                  }
                  currentRouteId={
                    currentRouteId ||
                    "home"
                  }
                />

                <ThemeToggle
                  theme={
                    theme
                  }
                  themes={
                    themes
                  }
                  setTheme={
                    setTheme
                  }
                  language={
                    language
                  }
                />
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}